/**
 * Page copy for /industries/healthcare.
 *
 * Source: page-industry-healthcare.md, supplied by the client.
 *
 * Decisions carried over from /industries/fintech, same reasoning:
 *
 * 1. METRICS. §6 tags AI Requirement Analysis as "a WeAreQA engagement", so the
 *    three figures ship here as a WeAreQA client outcome with no attribution
 *    label. The /industries hub keeps "Work our team has led" on its Healthcare
 *    block. The two pages differ deliberately.
 * 2. §4 HIPAA. Built verbatim. It carries the only dated claim on the page — a
 *    proposed Security Rule overhaul that has not landed — and the source asks
 *    for a re-check every quarter. Flagged in CLAUDE.md.
 * 3. SERVICE LINKS. The source's hrefs (/services/ai-testing-services,
 *    /services/test-automation-architecture, /services/managed-qa-services) are
 *    earlier slugs. Repointed at the routes that exist; labels unchanged.
 *    /industries/pharma does not exist yet and is held at "#".
 *
 * NO PLACEHOLDERS LEFT (2026-08-19). Client shape came from the deck, the price
 * slot was removed, and §4's HIPAA status was verified. See the notes at each.
 */

import type { IconName } from "../../components/ui/waq/Icon";

export const seo = {
  title: "Healthcare & HealthTech Software Testing",
  description:
    "Quality engineering for digital health. We work outside your PHI scope by default — synthetic patient data, no production access, no BAA needed.",
} as const;

export const hero = {
  breadcrumb: { label: "Industries", href: "/industries" },
  badge: "Healthcare & HealthTech",
  heading: "Quality Engineering for Digital Health",
  /** Split at the source's own sentence boundary; the second clause is the turn. */
  situation: [
    "A defect in your product isn't a bad user experience.",
    "It's a clinical event, and clinical events get reported.",
  ],
  pressure:
    "So you test carefully, which means slowly. Meanwhile your enterprise customers are asking for a release cadence you can't hit and evidence you don't have time to produce.",
  segmentLabel: "Segment",
  segments: ["Digital health / HealthTech", "Funded scaleups"],
} as const;

/**
 * §2 — the page. The source calls "we never touch your PHI" a stronger opening
 * than any HIPAA claim, and structurally true rather than aspirational.
 *
 * Two problems, one solution, so it renders as two problems and one solution.
 */
export const dataProblem = {
  heading: "How do you test healthcare software without patient data?",
  display: "The data problem nobody solves properly",
  problems: [
    {
      label: "Your test data",
      lead: "",
      strong: "",
      body: "You can't test with real patient data. So your test data is a handful of tidy fictional patients — and tidy fictional patients don't have three comorbidities, a fifteen-year record with two name changes, and a medication list that contradicts itself.",
      /** Its own paragraph in the source, and the consequence. */
      emphasis:
        "Which means the defects that matter only appear in production, where the data is real and the stakes are highest.",
      icon: "database",
    },
    {
      label: "Your vendor",
      lead: "The second half of the problem is your vendor. ",
      strong:
        "Anyone who touches PHI, including in a test environment, becomes a Business Associate",
      body: " — and now your counsel needs a signed BAA, your security team needs a questionnaire returned, and a six-week engagement has a four-week legal preamble.",
      emphasis: "",
      icon: "fileText",
    },
  ] as {
    label: string;
    lead: string;
    strong: string;
    body: string;
    emphasis: string;
    icon: IconName;
  }[],
  solution: {
    strong: "We solve both by staying outside your PHI scope by default.",
    body: " Synthetic patient data shaped to real clinical distributions — the comorbidities, the messy longitudinal records, the demographic edge cases your model handles badly. Generated, not sampled.",
    /** The sentence's own closing list, set as a list. No words changed. */
    stanceLabel: "The default engagement",
    stance: ["No production access", "No PHI", "No BAA"],
  },
  close:
    "That's not a compliance workaround. It's better testing, because synthetic data can be built to contain the cases your real data set happens not to include.",
} as const;

export type Item = { lead: string; tail: string; icon: IconName };

export const breaks = {
  heading: "What makes testing digital health products difficult?",
  display: "What actually breaks",
  items: [
    {
      lead: "Clinical logic is undertested.",
      tail: " Dosage calculations, risk scores, and care pathway branching get exercised on typical patients. The atypical ones are found by clinicians.",
      icon: "pulse",
    },
    {
      lead: "You integrate with systems you don't control.",
      tail: " HL7 and FHIR interfaces against EHRs that each interpret the standard slightly differently, with no test instance you can break.",
      icon: "network",
    },
    {
      lead: "Requirements are ambiguous and the ambiguity is dangerous.",
      tail: " In most software an unclear requirement produces a rework ticket. In clinical software it produces a defect that behaves correctly according to the spec and incorrectly according to medicine.",
      icon: "alert",
    },
    {
      lead: "Traceability is manual.",
      tail: " You need requirement-to-test-to-result evidence for enterprise customers and eventually for regulators, and someone is maintaining it in a spreadsheet.",
      icon: "fileText",
    },
    {
      lead: "Consent and access paths get less attention than clinical features.",
      tail: " Role-based access, consent withdrawal, and audit logging are where breaches actually originate.",
      icon: "shieldCheck",
    },
  ] as Item[],
} as const;

/**
 * §4 — the only dated claim on the site's healthcare page. It deliberately
 * refuses to manufacture urgency, so it is rendered calmly: no navy panel, no
 * accent bar on the proposal itself. Selling a slipping proposal as imminent
 * would be caught by any CTO who reads the trade press.
 */
export const hipaa = {
  display: "Where HIPAA sits in this",
  today:
    "HIPAA today is stable and well understood: safeguards around ePHI, access control, audit logging, breach notification. What matters for testing is scope — who touches PHI, and under what agreement.",
  proposal: {
    strong: "A significant Security Rule overhaul has been proposed",
    lead: " — encryption and MFA becoming required rather than addressable, 72-hour incident reporting, annual technical testing. As of now it is ",
    notFinal: "not final",
    tail: "; the timeline has slipped and finalisation is not expected imminently. Once it does land, entities get roughly 240 days.",
  },
  position:
    "We're not going to tell you that's an emergency. It isn't yet. It does mean that a testing programme built to produce evidence continuously will absorb the change without a scramble, and one built to produce evidence on demand won't.",
  /**
   * STATUS VERIFIED 2026-08-19. The placeholder asked for exactly this check
   * and it has now been done, so the on-page marker is gone. **The re-check is
   * still quarterly — next due November 2026** — and this section remains on
   * CLAUDE.md's launch-blocking list.
   *
   * What was confirmed, and it means the copy above is accurate as written:
   *
   *   - The NPRM was published in the Federal Register on 6 January 2025 and
   *     the comment period closed 7 March 2025.
   *   - It is STILL NOT FINAL. OCR has issued no final rule.
   *   - A final rule was expected around May 2026; that date has passed.
   *     **OMB now targets July 2027 for final action.**
   *   - It could still change, be delayed further, or be withdrawn.
   *   - The proposed content matches what `proposal` describes: encryption and
   *     MFA moving from addressable to required, 72-hour incident reporting,
   *     annual technical testing.
   *
   * NOT ADDED, and worth a decision: the July 2027 target could replace "the
   * timeline has slipped and finalisation is not expected imminently" with
   * something concrete. It would make the quarterly check trivial — one date to
   * confirm — and it reduces urgency rather than adding it, so it is consistent
   * with this section's deliberate refusal to call the proposal an emergency.
   * It also dates harder: if OMB moves the target the copy is simply wrong,
   * whereas "not imminently" stays true longer. Client's call.
   */
} as const;

export const capabilities = {
  display: "What we do",
  items: [
    {
      lead: "Synthetic clinical data.",
      tail: "Patient records built to real distributions, including the cases your production data doesn't contain. Provisioned per test run, torn down after.",
      icon: "database",
    },
    {
      lead: "Interface testing against unstable partners.",
      tail: "Contract tests for HL7 and FHIR integrations so an EHR's interpretation change surfaces in your pipeline rather than at a customer site.",
      icon: "network",
    },
    {
      lead: "Requirement analysis before test design.",
      tail: "Ambiguity found at the requirement stage costs a conversation. Found after release, it costs an incident report. This is where most of our value lands in clinical software.",
      icon: "search",
    },
    {
      lead: "Traceability generated automatically.",
      tail: "Requirement to test to result, produced by the pipeline. The evidence pack for an enterprise customer's due diligence becomes a report you run.",
      icon: "fileText",
    },
  ] as Item[],
  deliveryLead: "Delivered through ",
  delivery: [
    {
      label: "AI-Driven Validation",
      href: "/services/ai-driven-validation",
      tail: ", ",
    },
    {
      label: "Test Architecture",
      href: "/services/qa-architecture-engineering",
      tail: ", or ",
    },
    {
      label: "Intelligent Quality Operations",
      href: "/services/intelligent-quality-operations",
      tail: " depending on what the constraint turns out to be.",
    },
  ],
} as const;

export const proof = {
  display: "Proof",
  name: "AI Requirement Analysis",
  attribution: "A WeAreQA engagement",
  /**
   * From `WeAreQA-AI-Driven.pdf` slide 20, where this study appears in the AI
   * in Action Portfolio as "04 AI REQUIREMENT ANALYSIS — Healthcare Solutions
   * Provider". Same source as the shapes on /industries/fintech and
   * /industries/ecommerce.
   *
   * DELIBERATELY ABSENT: engineer count and product type. The placeholder asked
   * for both and its suggested wording — "A US remote patient monitoring
   * platform, ~120 engineers, releasing fortnightly" — was labelled "Example:"
   * because it is illustration. Factual claims about a real engagement; nobody
   * supplied the real ones; not invented here.
   */
  shape: "A healthcare solutions provider.",
  narrative:
    "Business requirements needed extensive manual review before anything could be tested, and ambiguity was reaching implementation. We put requirement analysis on models that flag ambiguity, contradiction, and missing acceptance criteria before test design starts.",
  metrics: [
    { value: "50%", label: "Faster requirement analysis" },
    { value: "45%", label: "Fewer requirement defects" },
    { value: "100%", label: "Traceability coverage" },
  ],
  /** The source singles out the middle figure. The page should too. */
  highlightIndex: 1,
  read: {
    strong: "The middle number is the clinical one.",
    tail: " A requirement defect in health software produces a system that works exactly as specified and wrongly as medicine. Catching 45% more of those before code is written is worth more here than in any other sector we work in.",
  },
  /**
   * NO "Read the full case study" LINK. Removed at the client's request.
   *
   * It was href="#" — it pointed at one of the five case studies the client
   * rejected as wrong and deleted, and no replacement has landed. /case-studies
   * is not a substitute: that hub lists the three AI use-case studies, not this
   * one. Same removal as /industries/fintech.
   *
   * If a replacement study ever lands for this industry, restore the link
   * pointing at the real route.
   */
} as const;

/**
 * §7 — no "after" column here, unlike fintech. This table only states what the
 * current state costs, so it renders as four costs rather than a comparison.
 */
export const businessCase = {
  display: "The business case",
  note: "For the conversation with your CEO or board.",
  columnLabel: "Cost of the current state",
  rows: [
    {
      label: "Clinical risk",
      cost: "A defect reaching patients is reportable, investigable, and public",
      icon: "alert",
    },
    {
      label: "Sales friction",
      cost: "Enterprise health customers run quality and security due diligence. Weak evidence slows deals or loses them",
      icon: "cost",
    },
    {
      label: "Release drag",
      cost: "Careful-therefore-slow means competitors ship what your customers asked you for",
      icon: "clock",
    },
    {
      label: "Regulatory readiness",
      cost: "Continuous evidence absorbs rule changes. On-demand evidence turns each one into a project",
      icon: "shieldCheck",
    },
  ] as { label: string; cost: string; icon: IconName }[],
  /** The source points at row two. It is the revenue argument, not the risk one. */
  highlightIndex: 1,
  close:
    "The second row is the one that shows up in the revenue number rather than the risk register, and it's usually the one that gets this funded.",
} as const;

export const notFit = {
  display: "Where we're not the right fit",
  items: [
    {
      lead: "Providers, payers, and hospital IT.",
      tail: " Different buyer, different procurement, different problem. We work with digital health companies.",
    },
    {
      lead: "Medical device software and SaMD.",
      tail: " FDA-regulated device software under IEC 62304 needs a specialist regulatory partner.",
    },
    {
      lead: "Engagements that need us inside your PHI scope.",
      tail: " We work with synthetic data and no production access by default, which is what lets us start in days. If yours genuinely requires a Business Associate, that is a different conversation and a slower start — not a no.",
    },
  ],
  close:
    "That default is deliberate. It's what lets us start in days rather than after a legal review.",
} as const;

/**
 * §9. NO PRICE, BY DECISION — consistent with the four service pages,
 * /industries/ecommerce and /industries/fintech.
 *
 * **WHAT IS LEFT IS A HEADING AND TWO BUTTONS.** No body copy; the placeholder
 * was the only thing between "Starting from" and the CTAs, so the section
 * announces a price and shows a call to action instead of one. It was always
 * CONSIDERED AND DECLINED, 2026-08-19: one line replacing `display` would have
 * made it the closing CTA it resembles. The client chose to leave the heading
 * as the source wrote it. Do not revisit without asking.
 *
 * Three of five industry pages are now in this state; pharma and communications
 * still hold the placeholder.
 */
export const pricing = {
  display: "Starting from",
} as const;

export const related = {
  display: "Related",
  items: [
    {
      label: "AI-Driven Validation",
      tail: "Requirement analysis and test design",
      href: "/services/ai-driven-validation",
    },
    {
      label: "Test Architecture & QA Platform Engineering",
      tail: "Synthetic data provisioning",
      href: "/services/qa-architecture-engineering",
    },
    {
      label: "Pharma",
      tail: "GxP and 21 CFR Part 11 validation",
      href: "/industries/pharma",
    },
    { label: "AI Maturity Model", tail: "", href: "/#assessment" },
  ],
} as const;
